/**
 * Etiquetas y textos por código del WhatsApp oficial.
 *
 * Los Records son EXHAUSTIVOS a propósito (no `Partial`): un estado o un código nuevo del backend
 * sin su texto rompe `tsc` en vez de caer a un fallback que muestre otra cosa. Es el mismo criterio
 * que `ChipEstado.tsx` de Activa: un «Borrador» pintado sobre una campaña que está enviando es
 * peor que un build roto.
 */

import type {
  EstadoPlantilla, EstadoCampanaOficial, EstadoMensajeOficial, MotivoExclusion, EsperaCampana,
  CodigoBloqueo, CodigoCorte, MotivoCorte, TipoMovimiento, Proposito, Categoria, TipoPlantilla, FuenteOptIn, FallidoPorCodigo,
  MotivoPlantillaInutilizable, PlantillasNoUsables,
} from "../types";
import type { PasoAsistente } from "./chequeo";
import { fechaDia, fechaHora, num, plural } from "../../campana-nueva/utils/format";
import { usd, usdHaciaArriba, usdPorMensaje } from "./formato";

const RUTA_CONFIG = "/activa/oficial/configuracion";

// ── Plantilla aprobada que no sirve con la cuenta conectada (A1-4, A1-6; textos de §6.3) ──
// Arriba de todo: BLOQUEO y CORTE los usan y un `const` no se puede leer antes de declararlo.
//
// Sin «Twilio» en lo que lee el comercio (plantillas, campañas, Inicio, Conversaciones): no sabe qué es. El que
// aprueba es Meta, por donde sale es WhatsApp y la cuenta es «la conectada». Twilio queda en «Conexión (técnico)»
// y en los «Detalle técnico» plegados (Estadísticas y el Resultado de una campaña), que es donde lo busca el equipo
// de Deenex.

export const TEXTO_PLANTILLA_SIMULADA = "Esta plantilla se aprobó en modo simulado: Meta nunca la vio. Duplicala y mandala a aprobación de verdad.";
export const TEXTO_PLANTILLA_OTRA_CUENTA = "Esta plantilla se creó con otra cuenta, no con la conectada, y no sirve con ella. Duplicala y mandala a aprobación.";
/** «No aprobada» significa sólo eso: en revisión, rechazada o archivada. Nunca «simulada». */
export const TEXTO_PLANTILLA_NO_APROBADA = "La plantilla no está aprobada por WhatsApp: sigue en revisión o la rechazaron.";
export const TEXTO_MOTIVO_PLANTILLA: Record<MotivoPlantillaInutilizable, string> = {
  PLANTILLA_SIMULADA: TEXTO_PLANTILLA_SIMULADA,
  PLANTILLA_DE_OTRA_CUENTA: TEXTO_PLANTILLA_OTRA_CUENTA,
};

export const ESTADO_PLANTILLA: Record<EstadoPlantilla, { label: string; cls: string; ayuda: string }> = {
  borrador: { label: "Borrador", cls: "bg-slate-100 text-slate-600", ayuda: "Todavía no se mandó a Meta." },
  // Decía «No llegó a Meta» (y el chip, en mayúsculas, «NO LLEGÓ A META»): sonaba a error y no decía qué falta.
  unsubmitted: { label: "Falta mandarla a aprobar", cls: "bg-slate-100 text-slate-600", ayuda: "Se armó, pero el pedido de aprobación no le llegó a Meta. Hay que volver a mandarla." },
  received: { label: "En revisión", cls: "bg-amber-50 text-amber-700", ayuda: "Meta la está revisando. Tarda de minutos a 48 h." },
  pending: { label: "En revisión", cls: "bg-amber-50 text-amber-700", ayuda: "Meta la está revisando. Tarda de minutos a 48 h." },
  approved: { label: "Aprobada", cls: "bg-emerald-50 text-emerald-700", ayuda: "Lista para usar en campañas." },
  rejected: { label: "Rechazada", cls: "bg-red-50 text-red-600", ayuda: "Meta no la aprobó. Duplicala, corregí y volvé a mandarla." },
  paused: { label: "Pausada por Meta", cls: "bg-orange-50 text-orange-700", ayuda: "Meta la pausó por quejas de quien la recibió: 3 h la primera vez, 6 h la segunda; a la tercera la deshabilita. Las campañas que la usan se frenaron solas." },
  disabled: { label: "Deshabilitada", cls: "bg-red-100 text-red-700", ayuda: "Meta la deshabilitó por calidad. No se puede volver a usar: hay que crear otra." },
  eliminada: { label: "Eliminada", cls: "bg-slate-100 text-slate-400 line-through", ayuda: "Se borró en WhatsApp. El nombre no se puede reusar por 30 días." },
};

export const ESTADO_CAMPANA: Record<EstadoCampanaOficial, { label: string; cls: string }> = {
  borrador: { label: "Borrador", cls: "bg-amber-50 text-amber-600" },
  programada: { label: "Programada", cls: "bg-indigo-50 text-indigo-600" },
  enviando: { label: "Enviando", cls: "bg-sky-50 text-sky-600" },
  pausada: { label: "Pausada", cls: "bg-slate-100 text-slate-600" },
  // «Frenada sola» no se entendía: se frenó por un problema (bajas, saldo, la plantilla, la marca) y el aviso dice cuál
  pausada_auto: { label: "Frenada por un problema", cls: "bg-red-50 text-red-600" },
  finalizada: { label: "Terminada", cls: "bg-emerald-50 text-emerald-700" },
  cancelada: { label: "Cancelada", cls: "bg-slate-100 text-slate-500" },
};

// Colores: emerald = bien, amber = atención, red = error o baja, sky = en camino / leído.
export const ESTADO_MENSAJE: Record<EstadoMensajeOficial, { label: string; cls: string }> = {
  // Sólo campañas de antes de sacar el grupo de control: esa persona quedó apartada y no recibió nada.
  control: { label: "apartado, no se mandó", cls: "bg-slate-100 text-slate-500" },
  pendiente: { label: "en cola", cls: "bg-slate-100 text-slate-600" },
  reservado: { label: "saliendo", cls: "bg-sky-50 text-sky-600" },
  desconocido: { label: "esperando confirmación", cls: "bg-amber-50 text-amber-700" },
  aceptado: { label: "saliendo hacia WhatsApp", cls: "bg-sky-50 text-sky-600" },
  enviado: { label: "enviado", cls: "bg-sky-50 text-sky-700" },
  entregado: { label: "entregado ✓✓", cls: "bg-emerald-50 text-emerald-700" },
  leido: { label: "leído ✓✓", cls: "bg-sky-100 text-sky-800" },
  fallido: { label: "falló", cls: "bg-red-50 text-red-600" },
  no_entregado: { label: "no se entregó", cls: "bg-red-50 text-red-600" },
  excluido: { label: "no se mandó", cls: "bg-slate-100 text-slate-500" },
  cancelado: { label: "cancelado", cls: "bg-slate-100 text-slate-400" },
  sin_confirmacion: { label: "sin confirmación", cls: "bg-amber-50 text-amber-700" },
};

export const MOTIVO_EXCLUSION: Record<MotivoExclusion, { titulo: string; detalle: string }> = {
  // El filtro «Por compras» de la audiencia (28/10/2026): no es una compuerta, es a quién se eligió mandarle.
  filtro_compras: { titulo: "No cumple el filtro de compras", detalle: "Lo deja afuera el filtro «Por compras» que elegiste en la audiencia." },
  // Las exclusiones de la audiencia (28/10/2026): también a quién se eligió NO mandarle. Con una sola lista, el título
  // la nombra («Está en la lista «Empleados»», `filasExclusion`).
  en_lista_exclusion: { titulo: "Está en una lista de exclusión", detalle: "Está en una de tus listas de exclusión: las que valen para todas las campañas o las que elegiste en la audiencia." },
  recibio_otra_campana: { titulo: "Recibió otra campaña elegida", detalle: "Le llegó una de las campañas que elegiste excluir en la audiencia." },
  // Antes eran «Teléfono inválido»: el dueño buscaba un número mal escrito y no había ninguno.
  sin_celular: { titulo: "Sin celular", detalle: "No tiene un celular cargado (o el que vino no se pudo leer)." },
  telefono_invalido: { titulo: "Teléfono inválido", detalle: "El número no tiene formato de celular." },
  numero_eeuu: { titulo: "Número de EE. UU.", detalle: "Meta no entrega marketing a números +1." },
  telefono_ambiguo: { titulo: "Teléfono dudoso", detalle: "Vino sin +54: no sabemos si es celular. Se puede incluir desde Configuración." },
  numero_invalido_previo: { titulo: "Sin WhatsApp", detalle: "Un envío anterior dijo que ese número no tiene WhatsApp." },
  // El mismo hecho que `TITULO_BAJA_MARKETING` del backend (codigos.util.ts): lo frenó la persona desde WhatsApp.
  baja_marketing: { titulo: "Frenó las promociones", detalle: "Un envío anterior dijo que dejó de recibir mensajes de marketing de esta marca: lo frenó desde WhatsApp." },
  no_contactar: { titulo: "En no contactar", detalle: "Pidió la baja por algún canal." },
  sin_optin: { titulo: "Sin consentimiento", detalle: "No hay constancia de que aceptó recibir mensajes (en el oficial sólo se escribe a quien dijo que sí)." },
  limitado_meta_24h: { titulo: "Frenado por Meta hoy", detalle: "Meta limitó el marketing a esa persona en las últimas 24 h." },
  // El detalle con los días y cuántos mensajes lo arma `detalleExclusion` (la regla de la marca, de su config).
  fatiga: { titulo: "Recibió otra promo hace poco", detalle: "Ya le llegó un mensaje hace pocos días, por WhatsApp oficial o por QR." },
  duplicado: { titulo: "Número repetido", detalle: "Aparece dos veces: se le escribe una sola." },
  variable_vacia: { titulo: "Le falta un dato", detalle: "No tiene el dato de una variable: no se le manda, salvo que elijas un texto en su lugar." },
};

/**
 * La regla de fatiga de la marca (29/10/2026, Configuración → Envío): «como mucho 1 mensaje cada 7 días por persona,
 * sumando WhatsApp oficial y por QR». La cuenta el backend con los dos canales (y los mails, si la marca los suma).
 */
export function textoReglaFatiga(dias: number, maximo = 1, contarMails = false): string {
  const d = Number(dias) || 0;
  if (d <= 0) return "Sin límite de mensajes por persona.";
  const k = Math.max(1, Math.floor(Number(maximo) || 1));
  const canales = contarMails ? "WhatsApp oficial, WhatsApp por QR y los mails" : "WhatsApp oficial y por QR";
  return `Como mucho ${num(k)} ${plural(k, "mensaje", "mensajes")} cada ${num(d)} ${plural(d, "día", "días")} por persona, sumando ${canales}.`;
}

/**
 * Lo que ya recibió quien queda afuera por la regla, después de la cantidad: «recibió un mensaje en los últimos 7 días,
 * por WhatsApp oficial o por QR» (con K > 1, «recibieron 2 mensajes …, sumando WhatsApp oficial y por QR»).
 */
export function textoYaRecibio(n: number, dias: number, maximo = 1): string {
  const d = Number(dias) || 0;
  const k = Math.max(1, Math.floor(Number(maximo) || 1));
  const ventana = d === 1 ? "en el último día" : `en los últimos ${num(d)} días`;
  const verbo = plural(n, "recibió", "recibieron");
  return k === 1 ? `${verbo} un mensaje ${ventana}, por WhatsApp oficial o por QR` : `${verbo} ${num(k)} mensajes ${ventana}, sumando WhatsApp oficial y por QR`;
}

/** Detalle de un motivo de exclusión con los datos de la marca (la fatiga dice cuántos días, cuántos mensajes y por dónde). */
export function detalleExclusion(m: MotivoExclusion, fatigaDias: number, fatigaMaximo = 1): string {
  if (m === "fatiga" && Number.isFinite(fatigaDias) && fatigaDias > 0) {
    const k = Math.max(1, Math.floor(Number(fatigaMaximo) || 1));
    const ventana = fatigaDias === 1 ? "en el último día" : `en los últimos ${num(fatigaDias)} días`;
    return k === 1
      ? `Ya le llegó un mensaje ${ventana}, por WhatsApp oficial o por QR.`
      : `Ya le llegaron ${num(k)} mensajes ${ventana}, sumando WhatsApp oficial y por QR: es lo que permite la marca.`;
  }
  return MOTIVO_EXCLUSION[m].detalle;
}

export const ESPERA: Record<Exclude<EsperaCampana, null>, { titulo: string; detalle: string; accion?: { label: string; ruta: string } }> = {
  fuera_de_franja: { titulo: "Fuera del horario permitido", detalle: "Sigue sola cuando se abra la franja." },
  cupo_24h: { titulo: "Llegó al límite de Meta de hoy", detalle: "Sigue sola cuando se libere cupo." },
  calidad_media: {
    // También le pasa a una campaña de utilidad con la calidad en roja: el título no dice «media».
    titulo: "Sale a la mitad del cupo por la calidad",
    detalle: "Meta bajó la calidad del número: las campañas salen a la mitad del cupo hasta que vuelva a Alta. Sigue sola cuando se libere cupo.",
    accion: { label: "Ver calentamiento", ruta: "/activa/oficial/calentamiento" },
  },
  calidad_baja: {
    titulo: "Marketing en pausa por la calidad del número",
    detalle: "Meta bajó la calidad del número a Baja: el marketing espera 7 días desde la última lectura en Baja y después sigue solo.",
    accion: { label: "Ver calentamiento", ruta: "/activa/oficial/calentamiento" },
  },
  saldo: { titulo: "El saldo puede no alcanzar", detalle: "Si se acaba, la campaña se pausa: cargá saldo y reanudala.", accion: { label: "Cargar saldo", ruta: `${RUTA_CONFIG}?seccion=saldo` } },
  tope_mensual: { titulo: "Llegó al tope de gasto del mes", detalle: "La campaña se pausa: subí el tope (o esperá al mes que viene) y reanudala.", accion: { label: "Ver tope", ruta: `${RUTA_CONFIG}?seccion=saldo` } },
  marca_frenada: { titulo: "La marca está frenada", detalle: "Ningún mensaje sale: reanudá la marca en Configuración y después la campaña.", accion: { label: "Ver por qué", ruta: `${RUTA_CONFIG}?seccion=cuenta` } },
  reintentos: { titulo: "Esperando reintentos", detalle: "Quedan mensajes que Meta frenó; se reintentan una sola vez pasadas 24 h." },
  tope_diario: { titulo: "Llegó a su tope del día", detalle: "Ya salieron los de hoy: sigue sola mañana, cuando abra su franja." },
};

/**
 * Detalle de una espera con los datos de la campaña: la franja (`franjasTexto`), cuándo sigue
 * (`esperaHasta`) y el límite de Meta. Sin esos datos, el texto genérico de `ESPERA`.
 */
export function detalleEspera(
  espera: Exclude<EsperaCampana, null>,
  i: { franjasTexto?: string | null; esperaHasta?: string | null; limite?: number | null; sinSalirAlFin?: number | null; hasta?: string | null } = {},
): string {
  // En su último día, lo que queda ya no sale (la próxima apertura es después de su fecha de fin): no hay «sigue sola».
  const n = Number(i.sinSalirAlFin) || 0;
  if (n > 0 && !i.esperaHasta && i.hasta && (espera === "fuera_de_franja" || espera === "cupo_24h" || espera === "calidad_media" || espera === "tope_diario")) {
    return `No sale nada más: su último día es el ${fechaDia(i.hasta)}. ${num(n)} ${plural(n, "persona de la cola no recibe", "personas de la cola no reciben")} el mensaje; a las 00:00 la campaña termina sola.`;
  }
  if (espera === "fuera_de_franja") {
    if (i.esperaHasta) return `Sigue sola el ${fechaHora(i.esperaHasta)}.`;
    return i.franjasTexto ? `Sigue sola cuando se abra la franja (${i.franjasTexto}).` : ESPERA.fuera_de_franja.detalle;
  }
  if (espera === "cupo_24h") {
    const quien = i.limite ? `Meta deja escribirle a ${num(i.limite)} personas distintas cada 24 h. ` : "";
    const cuando = i.esperaHasta ? `Sigue sola cuando se libere cupo, desde el ${fechaHora(i.esperaHasta)}.` : ESPERA.cupo_24h.detalle;
    return quien + cuando;
  }
  if (espera === "calidad_media" && i.esperaHasta) {
    return `Meta bajó la calidad del número: las campañas salen a la mitad del cupo hasta que vuelva a Alta. Sigue sola cuando se libere cupo, desde el ${fechaHora(i.esperaHasta)}.`;
  }
  if (espera === "tope_diario" && i.esperaHasta) return `Ya salieron los de hoy: sigue sola el ${fechaHora(i.esperaHasta)}.`;
  if (espera === "calidad_baja" && i.esperaHasta) {
    return `Meta bajó la calidad del número a Baja: el marketing espera hasta el ${fechaHora(i.esperaHasta)} (7 días desde la última lectura en Baja) y después sigue solo.`;
  }
  // Una espera que esta versión no conoce (un backend más nuevo) no rompe la pantalla.
  return ESPERA[espera]?.detalle ?? "";
}

export const BLOQUEO: Record<CodigoBloqueo, { titulo: string; detalle: string; accion?: { label: string; ruta?: string; paso?: PasoAsistente } }> = {
  VARIABLES_SIN_RESOLVER: {
    titulo: "Hay contactos sin el dato de una variable. Elegí un valor por defecto o excluirlos.",
    detalle: "Con una variable vacía el mensaje saldría roto.",
    accion: { label: "Ir a Variables", paso: "mensaje" },
  },
  FALTA_IMAGEN: {
    titulo: "Falta elegir la imagen de la campaña.",
    detalle: "La plantilla lleva una imagen que se elige en cada campaña: subí una o elegí una de las que ya están.",
    accion: { label: "Ir a Variables", paso: "mensaje" },
  },
  NADA_QUE_ENVIAR: {
    titulo: "Después de los filtros no queda nadie a quien escribirle.",
    detalle: "Revisá los grupos elegidos o los motivos de exclusión.",
    accion: { label: "Cambiar audiencia", paso: "audiencia" },
  },
  // Qué campaña y qué grupos lo dice el chequeo (`gruposEnOtraCampana`): lo agrega `tituloBloqueo`.
  GRUPOS_EN_OTRA_CAMPANA: {
    titulo: "Hay grupos que ya están en otra campaña de esta segmentación que salió.",
    detalle: "A esas personas les llegaría otro mensaje: elegí otros grupos, o armá la campaña con otra segmentación.",
    accion: { label: "Cambiar los grupos", paso: "audiencia" },
  },
  // El faltante en dólares lo agrega `tituloBloqueo`.
  SALDO_INSUFICIENTE: {
    titulo: "El saldo no alcanza.",
    detalle: "Se descuenta a medida que salen los mensajes: sin saldo, el envío se frena. Se carga en Configuración → Saldo y costos → «Cargar saldo».",
    accion: { label: "Cargar saldo", ruta: `${RUTA_CONFIG}?seccion=saldo` },
  },
  TOPE_MENSUAL: {
    titulo: "Esta campaña supera el tope de gasto del mes.",
    detalle: "Subí el tope o achicá la audiencia.",
    accion: { label: "Ver tope", ruta: `${RUTA_CONFIG}?seccion=saldo` },
  },
  CUENTA_NO_VERIFICADA: {
    titulo: "La cuenta de WhatsApp no está verificada.",
    detalle: "Sin una cuenta verificada no sale ningún mensaje.",
    accion: { label: "Ir a Configuración", ruta: `${RUTA_CONFIG}?seccion=cuenta` },
  },
  SIN_WEBHOOKS: {
    titulo: "Falta configurar la recepción de respuestas y bajas. Sin eso no se puede enviar.",
    detalle: "Sin webhooks no llegarían los recibos ni las bajas.",
    accion: { label: "Configurar webhooks", ruta: `${RUTA_CONFIG}?seccion=cuenta` },
  },
  SIN_PUBLIC_BASE_URL: {
    titulo: "El servidor no tiene URL pública (PUBLIC_BASE_URL). Avisale a quien administra el deploy.",
    detalle: "Sin URL pública, WhatsApp no tiene adónde mandar los recibos ni las bajas.",
  },
  MARCA_FRENADA: {
    titulo: "La marca está frenada.",
    detalle: "Ningún mensaje sale hasta que alguien la revise y la reanude.",
    accion: { label: "Ver por qué", ruta: `${RUTA_CONFIG}?seccion=cuenta` },
  },
  MARCA_DEMO: {
    titulo: "Esta es la marca de demostración: no se envía con credenciales reales.",
    detalle: "Elegí la marca real arriba de todo.",
  },
  SENDER_NO_ONLINE: {
    titulo: "El número de WhatsApp no está activo.",
    detalle: "Revisá el estado del número en Configuración.",
    accion: { label: "Ver número", ruta: `${RUTA_CONFIG}?seccion=cuenta` },
  },
  FALTA_HORARIO: {
    titulo: "Falta elegir el horario de la campaña.",
    detalle: "Con la franja de la marca o con una propia: se elige en el paso «Cuándo».",
    accion: { label: "Elegir el horario", paso: "cuando" },
  },
  FALTA_BAJA: {
    titulo: "Falta elegir cómo se dan de baja.",
    detalle: "Con el botón, con el texto (BAJA), con los dos o sin baja: se elige en el paso «Cuándo».",
    accion: { label: "Elegir la baja", paso: "cuando" },
  },
  HORARIO_NO_ALCANZA: {
    titulo: "Con este horario no sale nadie antes de la fecha de fin.",
    detalle: "Alargá la fecha de fin, la franja o el tope por día: se cambia en el paso «Cuándo».",
    accion: { label: "Cambiar el horario", paso: "cuando" },
  },
  BAJA_SIN_BOTON: {
    titulo: "La baja elegida es con el botón, y la plantilla no lo tiene.",
    detalle: "La campaña no le puede agregar un botón a lo que aprobó Meta: elegí otra forma de baja u otra plantilla.",
    accion: { label: "Cambiar la baja", paso: "cuando" },
  },
  BAJA_SIN_TEXTO: {
    titulo: "La baja elegida es con el texto, y la plantilla no dice BAJA.",
    detalle: "La campaña no le puede agregar un texto a lo que aprobó Meta: elegí otra forma de baja u otra plantilla.",
    accion: { label: "Cambiar la baja", paso: "cuando" },
  },
  PLANTILLA_NO_APROBADA: {
    titulo: "La plantilla todavía no está aprobada",
    detalle: "Cuando Meta la apruebe se habilitan la prueba y el envío.",
    accion: { label: "Ver la plantilla", paso: "mensaje" },
  },
  PLANTILLA_SIMULADA: {
    titulo: "La plantilla se aprobó en modo simulado: Meta nunca la vio.",
    detalle: TEXTO_PLANTILLA_SIMULADA,
    accion: { label: "Elegir otra plantilla", paso: "mensaje" },
  },
  PLANTILLA_DE_OTRA_CUENTA: {
    titulo: "La plantilla es de otra cuenta.",
    detalle: TEXTO_PLANTILLA_OTRA_CUENTA,
    accion: { label: "Elegir otra plantilla", paso: "mensaje" },
  },
};

/**
 * Título de un bloqueo con sus datos (el de saldo dice cuánto falta, al centavo hacia arriba: es lo que hay que cargar).
 * Si el saldo alcanzaba para esta campaña sola, dice cuánto tienen comprometido las que salen antes: sin eso, «faltan
 * USD 80» con USD 100 cargados parece un error. El de los grupos en otra campaña dice cuál y con qué grupos, como lo
 * escribe el chequeo (`gruposEnOtraCampana`).
 */
export function tituloBloqueo(
  code: CodigoBloqueo,
  i: { faltanteUSD?: number | null; comprometidoUSD?: number | null; gruposEnOtraCampana?: string | null } = {},
): string {
  if (code === "SALDO_INSUFICIENTE" && i.faltanteUSD !== null && i.faltanteUSD !== undefined && Number.isFinite(i.faltanteUSD)) {
    const comprometido = typeof i.comprometidoUSD === "number" && i.comprometidoUSD > 0 ? i.comprometidoUSD : 0;
    if (comprometido) return `El saldo no alcanza: faltan ${usdHaciaArriba(i.faltanteUSD)}, porque las campañas que salen antes todavía van a gastar hasta ${usdHaciaArriba(comprometido)}.`;
    return `El saldo no alcanza: faltan ${usdHaciaArriba(i.faltanteUSD)}.`;
  }
  if (code === "GRUPOS_EN_OTRA_CAMPANA" && i.gruposEnOtraCampana) return i.gruposEnOtraCampana;
  return BLOQUEO[code]?.titulo ?? `No se puede confirmar (${code}).`;
}

/** Dónde se carga el saldo de la marca: lo dicen los avisos de la prueba y el bloqueo de Confirmar. */
export const DONDE_CARGAR_SALDO = "Se carga en Configuración → Saldo y costos → «Cargar saldo».";

/**
 * La prueba de una campaña se cobra como un mensaje (el máximo, con el recargo) y el backend la corta con
 * SALDO_INSUFICIENTE si el saldo no lo cubre. Antes el comercio se enteraba al tocar «Mandar prueba», con «No
 * hay saldo para la prueba» y sin el monto. `null` si alcanza o si falta un dato (no se avisa a ciegas).
 */
export function avisoSaldoPrueba(i: { saldoUSD: number | null | undefined; costoUSD: number | null | undefined }): string | null {
  const saldo = Number(i.saldoUSD);
  const costo = Number(i.costoUSD);
  if (i.saldoUSD === null || i.saldoUSD === undefined || i.costoUSD === null || i.costoUSD === undefined) return null;
  if (!Number.isFinite(saldo) || !Number.isFinite(costo) || costo <= 0 || saldo >= costo) return null;
  return `Tu saldo (${usd(saldo)}) no alcanza para la prueba: cuesta hasta ${usdPorMensaje(costo)}. Cargá al menos ${usdHaciaArriba(costo - saldo)}. ${DONDE_CARGAR_SALDO}`;
}

/** El rechazo de la prueba por saldo: cuánto falta (si el backend lo mandó) y dónde se carga. */
export function textoSaldoInsuficientePrueba(faltanteUSD: unknown): string {
  const f = typeof faltanteUSD === "number" && Number.isFinite(faltanteUSD) && faltanteUSD > 0 ? faltanteUSD : null;
  return f === null ? `No hay saldo para la prueba. ${DONDE_CARGAR_SALDO}` : `No hay saldo para la prueba: faltan ${usdHaciaArriba(f)}. ${DONDE_CARGAR_SALDO}`;
}

/** Lo que hay que hacer después de un corte por bajas: va con el detalle del backend o con el propio. */
const REVISAR_BAJAS = "Revisá el mensaje y la audiencia antes de reanudar: al reanudar, las bajas se cuentan desde cero.";

export const CORTE: Record<CodigoCorte, { titulo: string; detalle: string }> = {
  bajas_3pct: {
    titulo: "Se frenó por bajas",
    detalle: `Las bajas de los que la recibieron pasaron el tope: el 3 % de los entregados, más 5 bajas. ${REVISAR_BAJAS}`,
  },
  sin_callbacks: { titulo: "Se frenó: WhatsApp no confirma nada", detalle: "Pasaron varios minutos sin un solo recibo. Revisá los webhooks en Configuración." },
  plantilla_pausada: { titulo: "Meta pausó la plantilla", detalle: "Meta la libera en 3 h o 6 h: cuando vuelva a estar aprobada, reanudá la campaña." },
  plantilla_deshabilitada: { titulo: "Meta deshabilitó la plantilla", detalle: "Hay que crear otra plantilla y otra campaña." },
  // Sin detalle propio: se usa el del backend (dice a qué estado pasó).
  plantilla_no_aprobada: { titulo: "La plantilla dejó de estar aprobada", detalle: "" },
  plantilla_de_otra_cuenta: { titulo: "La plantilla es de otra cuenta", detalle: TEXTO_PLANTILLA_OTRA_CUENTA },
  plantilla_simulada: { titulo: "La plantilla se aprobó en modo simulado", detalle: TEXTO_PLANTILLA_SIMULADA },
  sender_offline: { titulo: "El número de WhatsApp se desconectó", detalle: "Revisá el número en Configuración." },
  marca_frenada: { titulo: "Se frenó toda la marca", detalle: "Meta o el proveedor de WhatsApp frenaron los envíos de la marca. Ver Configuración." },
  // Sin detalle propio: el del backend dice qué falta (verificar, PUBLIC_BASE_URL, credenciales).
  cuenta_no_lista: { titulo: "La cuenta no puede enviar", detalle: "" },
  saldo: { titulo: "Se pausó: no hay saldo", detalle: "El saldo no alcanzó para el próximo mensaje. Cargá saldo y reanudala: no sigue sola." },
  tope_mensual: { titulo: "Se pausó: llegó al tope del mes", detalle: "Subí el tope de gasto (o esperá al mes que viene) y reanudala: no sigue sola." },
  programacion_vencida: { titulo: "No salió a horario", detalle: "El servidor estuvo caído más de 6 h: no se mandó sola. Revisá y reanudala si todavía tiene sentido." },
  twilio_sin_respuesta: {
    titulo: "Se pausó: WhatsApp no responde",
    detalle: "El proveedor de WhatsApp no respondió a 5 envíos seguidos. Esos mensajes quedan «esperando confirmación» y se concilian solos: no se reenvían. Revisá la conexión en Configuración y reanudala: no sigue sola.",
  },
  // Detalle propio: el del backend explica el cambio de modo, pero lo que el operador tiene que hacer es esto.
  modo_distinto: { titulo: "Campaña de prueba del modo simulado", detalle: "Con la cuenta real no se envía: cancelala y armá una nueva." },
};

/**
 * `modo_distinto` de una campaña REAL: se confirmó con la cuenta de Twilio y el servidor volvió a
 * simulado. No es una prueba, y cancelarla la pierde para cuando el servidor vuelva a Twilio.
 */
export const CORTE_MODO_DISTINTO_REAL: { titulo: string; detalle: string } = {
  titulo: "Campaña real con el servidor en simulado",
  detalle: "Se confirmó con la cuenta real; con el servidor en simulado no se envía. Queda frenada hasta que el servidor vuelva al modo real: si la cancelás, ya no se puede reanudar.",
};

/**
 * Un corte `modo_distinto` de cuando el servidor estaba en el otro modo, que volvió al de la campaña:
 * el backend ya la deja reanudar (`otroModo: false`). Con el texto de siempre («cancelala y armá una
 * nueva») el operador perdía una campaña que se podía seguir mandando.
 */
export const CORTE_MODO_YA_COINCIDE: { titulo: string; detalle: string } = {
  titulo: "Se frenó por un cambio de modo del servidor",
  detalle: "El servidor volvió al modo con el que se confirmó: ya se puede reanudar.",
};

/**
 * El texto de `modo_distinto` según de qué modo es la campaña: `simulado: false` es una real con el
 * servidor en simulado; si no, la de prueba del modo simulado con el servidor en twilio (lo de siempre,
 * también cuando no se sabe).
 */
export function textoModoDistinto(simulado: boolean | null | undefined): { titulo: string; detalle: string } {
  return simulado === false ? CORTE_MODO_DISTINTO_REAL : CORTE.modo_distinto;
}

/**
 * Un backend anterior a `modo_distinto` cortaba por el cambio de modo con `marca_frenada` y un
 * detalle que habla del modo («se confirmó en modo simulado y el servidor ahora está en modo real»).
 * Con el texto de `marca_frenada` («Meta o Twilio frenaron los envíos de la marca») el operador iba
 * a Configuración, donde la marca no está frenada.
 */
const RE_CORTE_POR_MODO = /modo (simulado|real)/i;

/**
 * Título y detalle de un corte. `CORTE` es exhaustivo, pero si igual llegara un código que no
 * conocemos (backend más nuevo que el front), se muestra el `detalle` del backend: nunca un corte
 * sin explicación. Con la campaña, `modo_distinto` dice si es la de prueba o una real.
 */
export function textoCorte(m: MotivoCorte | null, campana?: { simulado?: boolean | null; otroModo?: boolean }): { titulo: string; detalle: string } | null {
  if (!m) return null;
  if (m.codigo === "modo_distinto" || (m.codigo === "marca_frenada" && RE_CORTE_POR_MODO.test(m.detalle || ""))) {
    return campana?.otroModo === false ? CORTE_MODO_YA_COINCIDE : textoModoDistinto(campana?.simulado);
  }
  const t = (CORTE as Record<string, { titulo: string; detalle: string } | undefined>)[m.codigo];
  if (!t) return { titulo: "Se frenó por un problema", detalle: m.detalle || "" };
  if (m.codigo === "sin_callbacks") {
    // El backend dice cuántos minutos pasaron; si lo trae, se usa el número exacto.
    const min = /(\d+)\s*min/i.exec(m.detalle || "");
    if (min) return { titulo: t.titulo, detalle: `${plural(Number(min[1]), "Pasó", "Pasaron")} ${min[1]} ${plural(Number(min[1]), "minuto", "minutos")} sin un solo recibo. Revisá los webhooks en Configuración.` };
  }
  // El del backend trae las bajas, los entregados y el tope; el texto fijo los tapaba. Un corte viejo
  // («… : más del 3 %.») también se muestra con sus números: eran ciertos cuando cortó.
  const delBackend = (m.detalle || "").trim();
  if (m.codigo === "bajas_3pct" && delBackend) return { titulo: t.titulo, detalle: `${delBackend} ${REVISAR_BAJAS}` };
  return { titulo: t.titulo, detalle: t.detalle || m.detalle || "" };
}

/**
 * El corte que se muestra de una campaña. Con `otroModo` (C1: se confirmó en otro modo que el del
 * servidor) manda `modo_distinto` aunque el documento guarde otro código (el viejo `marca_frenada`)
 * o ninguno: esa campaña no sale nunca con la cuenta de hoy, diga lo que diga su corte.
 */
export function motivoCorteVisible(c: { otroModo?: boolean; motivoCorte: MotivoCorte | null }): MotivoCorte | null {
  if (c.otroModo !== true) return c.motivoCorte;
  return { codigo: "modo_distinto", detalle: c.motivoCorte?.detalle || "", at: c.motivoCorte?.at || "" };
}

export const TIPO_MOVIMIENTO: Record<TipoMovimiento, string> = {
  recarga: "Recarga",
  ajuste: "Ajuste",
  consumo: "Envío",
  reintegro: "Reintegro",
  consumo_entrante: "Respuesta recibida",
  consumo_sesion: "Mensaje de servicio",
};

const CALIDAD_BASE: Record<string, { label: string; cls: string }> = {
  GREEN: { label: "Alta", cls: "bg-emerald-50 text-emerald-700" },
  YELLOW: { label: "Media", cls: "bg-amber-50 text-amber-700" },
  RED: { label: "Baja", cls: "bg-red-50 text-red-600" },
  UNKNOWN: { label: "Sin dato", cls: "bg-slate-100 text-slate-500" },
};
/**
 * Lo que manda Twilio de verdad en Senders v2 (`properties.quality_rating`, ej. `"HIGH"`, F8). El
 * backend lo normaliza a GREEN/YELLOW/RED (A1-3); si igual llega crudo (un backend viejo, o sólo
 * `calidadRaw`), acá también se entiende. Antes, «HIGH» se mostraba «Sin dato».
 */
const CALIDAD_TWILIO: Record<string, string> = { HIGH: "GREEN", MEDIUM: "YELLOW", LOW: "RED" };

/**
 * Calidad del número según Meta. Cualquier valor que no conocemos se lee «Sin dato». Con
 * `calidadRaw` (lo crudo de Twilio) se usa ése cuando la calidad normalizada no dice nada.
 */
export function calidadInfo(calidad?: string | null, calidadRaw?: string | null): { label: string; cls: string } {
  const leer = (x?: string | null) => {
    const k = String(x || "").trim().toUpperCase();
    return CALIDAD_BASE[CALIDAD_TWILIO[k] ?? k];
  };
  const normal = leer(calidad);
  if (normal && normal !== CALIDAD_BASE.UNKNOWN) return normal;
  return leer(calidadRaw) ?? CALIDAD_BASE.UNKNOWN;
}

/** Estado del número de Senders v2 en castellano (Resumen y Estadísticas). Uno que no conocemos se muestra crudo. */
export const ESTADO_NUMERO: Record<string, string> = {
  ONLINE: "Activo",
  "ONLINE:UPDATING": "Activo (Meta lo está actualizando)",
  OFFLINE: "Fuera de línea",
};

/**
 * `CALIDAD[x]` nunca devuelve undefined: la calidad llega de Twilio como texto abierto y un valor
 * nuevo («FLAGGED», por ejemplo) no puede romper la pantalla del Resumen con un
 * `Cannot read properties of undefined`. Cae al respaldo «Sin dato».
 */
export const CALIDAD: Record<string, { label: string; cls: string }> = new Proxy(CALIDAD_BASE, {
  get: (_t, k) => (typeof k === "string" ? calidadInfo(k) : undefined),
});

export const PROPOSITO: Record<Proposito, { titulo: string; detalle: string; categoriaSugerida: Categoria }> = {
  promocion: { titulo: "Promoción o reactivación", detalle: "Descuentos, te extrañamos, novedades.", categoriaSugerida: "MARKETING" },
  aviso: { titulo: "Aviso de pedido o cuenta", detalle: "Cambio de horario, estado de un pedido, encuestas.", categoriaSugerida: "UTILITY" },
  beneficio: { titulo: "Cumpleaños o beneficio", detalle: "Regalos, puntos que vencen, beneficios.", categoriaSugerida: "MARKETING" },
};

export const TIPO_PLANTILLA: Record<TipoPlantilla, { titulo: string; detalle: string }> = {
  "twilio/text": { titulo: "Texto", detalle: "Sólo texto, sin botones." },
  "twilio/media": { titulo: "Imagen con texto", detalle: "Una imagen pública (https) arriba del texto, sin botones." },
  "twilio/quick-reply": { titulo: "Botones de respuesta rápida", detalle: "Hasta 10 botones que la persona toca para responder." },
  "twilio/call-to-action": { titulo: "Botón con link o teléfono", detalle: "Hasta 2 links y 1 teléfono." },
  "whatsapp/card": { titulo: "Tarjeta con imagen o link", detalle: "Imagen o título arriba, pie aparte y hasta 10 botones: de respuesta y hasta 2 links." },
};

export const CATEGORIA: Record<Categoria | "AUTHENTICATION", string> = {
  MARKETING: "Marketing",
  UTILITY: "Utilidad",
  AUTHENTICATION: "Autenticación",
};

/** Fuente del consentimiento, en castellano (Bajas y consentimientos, constancia). */
export const FUENTE_OPTIN: Record<FuenteOptIn, string> = {
  csv: "archivo",
  formulario: "formulario",
  compra: "compra",
  declaracion_marca: "declarado por la marca",
  desconocido: "sin fuente",
};

/** Qué hacer ante un rechazo de Meta, según el motivo. Se muestra SIEMPRE junto al motivo crudo. */
export function accionRechazo(rejectionReason?: string): string {
  const r = String(rejectionReason || "").toUpperCase();
  if (r.includes("INVALID_FORMAT")) return "El formato no pasó: revisá variables, espacios y emojis con el validador.";
  if (r.includes("TAG_CONTENT_MISMATCH")) return "Meta cree que la categoría no corresponde: si es una promo, tiene que ser Marketing.";
  if (r.includes("DUPLICATE")) return "Ya existe una igual en la cuenta.";
  if (r.includes("PROMOTIONAL")) return "Tiene contenido promocional: mandala como Marketing.";
  return "Revisá el texto, duplicala y volvé a mandarla.";
}

/**
 * Si una fila de «Fallidos por motivo» puede decir «No se cobra». El backend marca `exento` por
 * código, pero la exención sólo vale para `fallido`: a un `no_entregado` se le retiene el cargo de
 * Twilio (USD 0,005, más el recargo) sea cual sea el código (costo.util.ts). Fail-closed: si la fila
 * no dice que es `fallido` (agrupada por código sin estado), no se promete nada sobre la plata.
 */
export function fallidoSinCargo(f: Pick<FallidoPorCodigo, "exento" | "estado">): boolean {
  return f?.exento === true && f?.estado === "fallido";
}

// ── El motivo de un fallo, para una persona de un cliente ─────────────────────────────────────

/** El motivo de un fallo del que el backend no tiene título propio (un código que no conoce, o ninguno). */
export const MOTIVO_FALLO_SIN_DETALLE = "WhatsApp no lo entregó y no dio más detalle";
/** «Qué se hace» cuando lo que hay que hacer lo hace el equipo (revisar la cuenta, la consola, avisar a soporte). */
export const ACCION_FALLO_DEL_EQUIPO = "Lo revisa el equipo de Deenex.";

/**
 * El motivo de un fallo (el título de `codigos.util.ts` del backend, también el `motivoError` de cada mensaje) como lo
 * lee una persona de un cliente (Accesos, rol «comercio»): en palabras, sin el código ni el proveedor. El equipo ve el
 * título tal cual y el código al lado. Un código que el backend no conoce llega «Error de Twilio 12345» (con el número
 * adentro del texto), y el límite de la cuenta, «Se alcanzó el límite diario de la cuenta de Twilio».
 */
export function motivoFalloParaCliente(titulo: unknown): string {
  const t = typeof titulo === "string" ? titulo.trim() : "";
  if (!t || /^error de twilio\b/i.test(t)) return MOTIVO_FALLO_SIN_DETALLE;
  return t.replace(/\bla cuenta de Twilio\b/gi, "la cuenta de WhatsApp").replace(/\bTwilio\b/gi, "el proveedor de WhatsApp");
}

/**
 * «Qué se hace» con un fallo, para una persona de un cliente: lo que pide revisar algo de Twilio, su consola o avisar a
 * soporte lo hace el equipo. Si antes de los dos puntos dice qué pasó («Se frenaron todas las campañas de la marca:
 * revisá el límite de la cuenta en Twilio»), eso se queda.
 */
export function accionFalloParaCliente(accion: unknown): string {
  const a = typeof accion === "string" ? accion.trim() : "";
  const delEquipo = /twilio|consola|soporte/i;
  if (!delEquipo.test(a)) return a;
  const i = a.indexOf(":");
  const quepaso = i > 0 ? a.slice(0, i).trim() : "";
  return quepaso && !delEquipo.test(quepaso) ? `${quepaso}: lo revisa el equipo de Deenex.` : ACCION_FALLO_DEL_EQUIPO;
}

// ── Plantillas simuladas o de otra cuenta (A1-4 / A1-6) ─────────────────────────────────────

export const CHIP_PLANTILLA = {
  simulada: { label: "Simulada", cls: "bg-amber-50 text-amber-700", ayuda: "Se aprobó en modo simulado: Meta nunca la vio." },
  deOtraCuenta: { label: "De otra cuenta", cls: "bg-orange-50 text-orange-700", ayuda: "Se creó con otra cuenta, no con la conectada." },
} as const;

/**
 * La MISMA regla que el backend (`motivoPlantillaInutilizable` de `uso-plantilla.util.ts`): el código
 * por el que una plantilla aprobada no sirve con la cuenta conectada, o null.
 * - simulada: sólo en modo Twilio (en simulado es justo la que se usa para probar);
 * - de otra cuenta: en cualquier modo (el backend ya la marca comparando los Account SID).
 * El front deshabilita la opción para no dejar armar una campaña que el backend va a rechazar; el
 * backend igual la rechaza con el mismo código si el dato no llegó acá.
 */
export function motivoPlantillaInutilizable(
  p: { simulado?: boolean; deOtraCuenta?: boolean } | null | undefined,
  modo: string | null | undefined,
): MotivoPlantillaInutilizable | null {
  if (!p) return null;
  if (modo === "twilio" && p.simulado === true) return "PLANTILLA_SIMULADA";
  if (p.deOtraCuenta === true) return "PLANTILLA_DE_OTRA_CUENTA";
  return null;
}

/** El chip de estado de una aprobada en simulado cuando el servidor ya está en twilio: no es «Aprobada». */
export const ESTADO_APROBADA_EN_SIMULADO = {
  label: "Aprobada en simulado", cls: "bg-amber-50 text-amber-700",
  ayuda: "Se aprobó en modo simulado: Meta nunca la vio.",
} as const;

/** Cómo sale de ese estado: la copia es la que va a Meta de verdad (la simulada no se puede reenviar). */
export const TEXTO_GUIA_MANDAR_A_META = "Duplicala: la copia se crea con la cuenta conectada y la mandás a aprobación de Meta. Cuando Meta la apruebe, usala en una campaña nueva.";

/**
 * Chip de estado de una plantilla con la regla de `motivoPlantillaInutilizable`: una simulada con el
 * servidor en twilio dice «Aprobada en simulado» (ámbar), nunca «Aprobada». En simulado es la
 * plantilla de la demo y dice lo de siempre.
 */
export function estadoPlantillaVisible(
  p: { estado: EstadoPlantilla; simulado?: boolean; deOtraCuenta?: boolean },
  modo: string | null | undefined,
): { label: string; cls: string; ayuda: string } {
  if (p.estado === "approved" && motivoPlantillaInutilizable(p, modo) === "PLANTILLA_SIMULADA") return ESTADO_APROBADA_EN_SIMULADO;
  return ESTADO_PLANTILLA[p.estado];
}

/**
 * El recuadro de estado del detalle. Una aprobada que no sirve con la cuenta conectada no dice
 * «Lista para usar en campañas.»: dice por qué no sirve y cómo mandarla a Meta de verdad.
 */
export function ayudaEstadoPlantilla(
  p: { estado: EstadoPlantilla; simulado?: boolean; deOtraCuenta?: boolean },
  modo: string | null | undefined,
): string {
  const motivo = p.estado === "approved" ? motivoPlantillaInutilizable(p, modo) : null;
  if (motivo === "PLANTILLA_SIMULADA") return `${ESTADO_APROBADA_EN_SIMULADO.ayuda} ${TEXTO_GUIA_MANDAR_A_META}`;
  if (motivo) return TEXTO_MOTIVO_PLANTILLA[motivo];
  return ESTADO_PLANTILLA[p.estado]?.ayuda ?? `Estado: ${p.estado}`;
}

/**
 * El tile «Plantillas aprobadas» del Resumen: las que el backend dejó fuera de la cuenta porque no
 * sirven con la cuenta real («1 simulada: no sirve con la cuenta real»). null si no hay ninguna.
 */
export function textoNoUsables(n: PlantillasNoUsables | null | undefined): string | null {
  const sim = Number(n?.simuladas) || 0;
  const otra = Number(n?.deOtraCuenta) || 0;
  const partes: string[] = [];
  if (sim > 0) partes.push(sim === 1 ? "1 simulada: no sirve con la cuenta real" : `${num(sim)} simuladas: no sirven con la cuenta real`);
  if (otra > 0) partes.push(otra === 1 ? "1 de otra cuenta: no sirve con esta cuenta" : `${num(otra)} de otra cuenta: no sirven con esta cuenta`);
  return partes.length ? partes.join(" · ") : null;
}

/** El chip de un mensaje o una baja del modo simulado en la constancia (se usa para contestar un reclamo). */
export const CHIP_SIMULADO_NO_SALIO = "simulado: no salió a WhatsApp";

/** El texto de `motivoPlantillaInutilizable` (lo que muestran la lista, el selector y el asistente). */
export function motivoPlantillaNoUsable(p: { simulado?: boolean; deOtraCuenta?: boolean } | null | undefined, modo: string | null | undefined): string | null {
  const m = motivoPlantillaInutilizable(p, modo);
  return m ? TEXTO_MOTIVO_PLANTILLA[m] : null;
}

/**
 * Texto de un rechazo del backend por la plantilla (`code` de un 422), o null si no es de plantilla.
 * Cada código dice lo suyo: una simulada no «dejó de estar aprobada».
 */
export function textoRechazoPlantilla(code: string | null | undefined): string | null {
  if (code === "PLANTILLA_SIMULADA" || code === "PLANTILLA_DE_OTRA_CUENTA") return TEXTO_MOTIVO_PLANTILLA[code];
  if (code === "PLANTILLA_NO_APROBADA") return TEXTO_PLANTILLA_NO_APROBADA;
  return null;
}

// ── Alertas de la cuenta de Twilio (§6.6) ───────────────────────────────────────────────────

export type CodigoAlertaTwilio = "saldo_twilio_bajo" | "gasto_twilio_diario" | "gasto_twilio_mensual" | "calidad_media" | "calidad_baja"
  | "plantillas_otra_cuenta" | "pedido_de_alta" | "diagnostico_con_errores" | "cobertura_twilio";

const esNum = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);

/**
 * Texto de una alerta nueva del Resumen con sus datos. El backend ya manda `mensaje`; esto es el
 * respaldo cuando llega vacío (y lo que usa el banner de la conexión). Sin el número, la frase se
 * arma sin él: nunca «USD NaN» ni «undefined problemas».
 */
export function textoAlertaTwilio(codigo: CodigoAlertaTwilio, d: { n?: number | null; usd?: number | null } = {}): string {
  const n = esNum(d.n) ? d.n : null;
  const monto = esNum(d.usd) ? usd(d.usd) : null;
  switch (codigo) {
    case "saldo_twilio_bajo":
      return monto ? `El saldo de la cuenta del proveedor de WhatsApp es ${monto}: cargale saldo para que no se corten los envíos.`
        : "El saldo de la cuenta del proveedor de WhatsApp está bajo: cargale saldo para que no se corten los envíos.";
    case "gasto_twilio_diario":
      return `El proveedor de WhatsApp avisó que el gasto del día (horario UTC) superó ${monto ?? "el tope que configuraste"}.`;
    case "gasto_twilio_mensual":
      return `El proveedor de WhatsApp avisó que el gasto del mes (horario UTC) superó ${monto ?? "el tope que configuraste"}.`;
    case "calidad_media":
      return "Meta bajó la calidad del número a Media: bajá el ritmo y revisá las plantillas con más bajas.";
    case "calidad_baja":
      return "Meta bajó la calidad del número a Baja: pausá el marketing hasta que mejore.";
    case "plantillas_otra_cuenta":
      return n !== null
        ? `${num(n)} ${n === 1 ? "plantilla es de otra cuenta y no se puede usar" : "plantillas son de otra cuenta y no se pueden usar"}.`
        : "Hay plantillas de otra cuenta que no se pueden usar.";
    case "pedido_de_alta":
      return n !== null
        ? `${num(n)} ${n === 1 ? "persona pidió" : "personas pidieron"} volver a recibir mensajes (ALTA): revisalas en Bajas.`
        : "Hay personas que pidieron volver a recibir mensajes (ALTA): revisalas en Bajas.";
    case "diagnostico_con_errores":
      return n !== null && n > 0
        ? `La conexión de WhatsApp tiene ${num(n)} ${n === 1 ? "problema" : "problemas"}: revisalos en Configuración.`
        : "La conexión de WhatsApp tiene problemas: revisalos en Configuración.";
    case "cobertura_twilio":
      // `usd`: lo que falta para cubrir lo cargado
      return monto ? `El saldo de la cuenta del proveedor de WhatsApp no alcanza para lo que hay cargado en la plataforma: faltan ${monto}. Cargá la diferencia en la cuenta del proveedor para que no se corten los envíos.`
        : "El saldo de la cuenta del proveedor de WhatsApp no alcanza para lo que hay cargado en la plataforma: cargale saldo para que no se corten los envíos.";
  }
}

// ── Link Shortening (§6.5) ──────────────────────────────────────────────────────────────────

/** §6.3: el PATCH que prende Link Shortening sin dominio de acortado en Twilio vuelve 422 con este código. */
export const TEXTO_LINK_SHORTENING_SIN_DOMINIO = "El Messaging Service no tiene un dominio de acortado configurado en Twilio. Configuralo en la consola antes de prenderlo.";

/** Texto de un error al guardar la configuración de envío: los códigos con texto propio, o el del backend. */
export function textoErrorConfig(x: { code?: string; status: number; error?: string }): string | null {
  return x?.code === "LINK_SHORTENING_SIN_DOMINIO" ? TEXTO_LINK_SHORTENING_SIN_DOMINIO : null;
}

export const TEXTO_LINK_SHORTENING = "Twilio acorta y mide los links de los mensajes de servicio. En los botones de las plantillas aprobadas (las campañas) la documentación no asegura que mida clics. Necesita un dominio propio configurado en Twilio y cuesta USD 0,015 por mensaje, con los primeros 1.000 del mes sin cargo (página de precios de Twilio). Los links vencen a los 90 días.";
export const TEXTO_LINK_SHORTENING_CAMPANAS = "Probalo primero con una campaña chica: si Twilio rechaza el envío, apagalo.";
export const TEXTO_CLICS_VISTAS_PREVIAS = "Los clics pueden incluir las vistas previas que arma el teléfono al recibir un link: Twilio no las separa en este aviso.";

// ── Medición de la recompra, sin grupo de control ────────────────────────────────────────────
// Decisión de Alan: WhatsApp oficial no tiene grupo de control. La tarjeta dice SIEMPRE qué mide y
// qué no: sin esa aclaración, «volvieron X» se lee como el efecto del mensaje y lo infla.

export const TITULO_MEDICION = "¿Volvieron a comprar?";
export const SUBTITULO_MEDICION = "Entre los que recibieron el mensaje";
/** Con un público de la Base o el Público de Activa: un contacto de la base puede no haber comprado nunca (no «vuelve»). */
export const TITULO_MEDICION_BASE = "¿Compraron después del mensaje?";
export const SUBTITULO_MEDICION_BASE = "Con tu base de ventas, entre los que recibieron el mensaje";
// ── La constancia de un contacto (Bajas y consentimientos) ───────────────────────────────────

/** Cuántos mensajes y envíos de Conversaciones manda la constancia (`.limit(20)` en `constanciaContacto` del backend). */
export const TOPE_CONSTANCIA = 20;
/** La constancia promete lo que se le mandó a la persona y cortaba en 20 sin decirlo. */
export const TEXTO_CONSTANCIA_RECORTADA = `Se muestran los ${TOPE_CONSTANCIA} más recientes: puede haber más.`;
/** Busca en los archivos de clientes, la Base de datos, las bajas y lo que se le mandó: decía «en ninguna base de la marca». */
export const TEXTO_CONSTANCIA_VACIA = "No encontramos nada de ese número: ni consentimientos, ni bajas, ni mensajes de la marca.";

/** La medición se hizo con las compras de antes («pendiente-base-nueva»): decía «Falta una base posterior al envío». */
export const TEXTO_FALTAN_COMPRAS_POSTERIORES = "Para cerrar la medición faltan las compras de después del envío.";
export const TEXTO_MEDICION_SIN_CONTROL =
  "Cuenta quiénes volvieron a comprar entre los que recibieron el mensaje, sin grupo de comparación: no mide el efecto incremental del mensaje (parte de esta gente habría vuelto igual).";

/**
 * El punto de equilibrio del paso Confirmar es la otra punta de la misma medición: la cuenta sólo
 * vale para los que compran POR el mensaje, y la medición después muestra la recompra cruda (con los
 * que habrían vuelto igual). Sin esta aclaración el operador compara las dos cifras y concluye que la
 * campaña «se pagó sola», que es la lectura incremental que la medición no puede sostener.
 */
export const TEXTO_EQUILIBRIO_SIN_CONTROL =
  "Son compras que tienen que pasar gracias al mensaje, además de los que habrían vuelto igual. Sin grupo de comparación no se va a poder medir si se alcanzó: la recompra que muestra la medición incluye a los que habrían vuelto igual.";

/**
 * El mismo punto de equilibrio con un público de la base o el Público de Activa. Hasta el 27/10/2026 esas campañas no
 * tenían medición y esto lo decía; ahora se miden con la base de ventas en uso, también sin grupo de comparación: es
 * la misma aclaración, con «comprar» (un contacto de la base puede no haber comprado nunca) y la base de ventas.
 */
export const TEXTO_EQUILIBRIO_BASE =
  "Son compras que tienen que pasar gracias al mensaje. Sin grupo de comparación no se va a poder medir si se alcanzó: después del envío, la campaña cuenta quiénes compraron según tu base de ventas, también los que habrían comprado igual.";

/** La tarjeta de la medición con la base de ventas: lo mismo que `TEXTO_MEDICION_SIN_CONTROL`, con «compraron». */
export const TEXTO_MEDICION_BASE_SIN_CONTROL =
  "Cuenta quiénes compraron entre los que recibieron el mensaje, sin grupo de comparación: no mide el efecto incremental del mensaje (parte de esta gente habría comprado igual).";
/** Lo que lee una persona de un cliente cuando falta la base de ventas: subirla es del equipo (`/activa/publico/subir`). */
export const TEXTO_BASE_LA_SUBE_EL_EQUIPO =
  "La base de ventas la sube el equipo de Deenex: cuando tenga los pedidos de después del envío, vas a ver acá quién compró.";

/** Advertencias del backend que la tarjeta ya dice con `TEXTO_MEDICION_SIN_CONTROL`, o que eran de
 *  la comparación contra el control de las mediciones viejas: no se repiten ni se muestran. */
export function advertenciasMedicion(xs: readonly string[] | null | undefined): string[] {
  return (xs || []).filter((a) => {
    const t = String(a || "").trim().toLowerCase();
    return t !== "" && !t.startsWith("sin grupo de comparación") && t !== "sin grupo de control" && !t.startsWith("control chico");
  });
}
